"use client";

import React, { useEffect, useState } from "react";

import Image from "next/image";
// CMS-supplied hrefs are emitted verbatim - do NOT swap this for @repo/ui/navigation.
// The CMS localizes its own URLs (e.g. "cl/es/centro-de-ayuda/") and also returns
// external/relative ones, so prefixing here would double or corrupt the locale.
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Copyright } from "lucide-react";

import { Accordion, AccordionContent } from "@repo/ui/components/ui/accordion";
import {
  FooterAccordionItem,
  FooterAccordionTrigger,
} from "@repo/ui/components/ui/footerAccordion";
import { INITIAL_COUNT } from "@repo/ui/constants/footer";
import { cn, isPageNavigationHidden } from "@repo/ui/lib/utils";

import Certification_Logo_1 from "../../assets/folder-certifications/footer_logo_1.jpg";
import Certification_Logo_2 from "../../assets/folder-certifications/footer_logo_2.jpg";
import Certification_Logo_3 from "../../assets/folder-certifications/footer_logo_3.jpg";
import Certification_Logo_4 from "../../assets/folder-certifications/footer_logo_4.jpg";
import Certification_Logo_5 from "../../assets/folder-certifications/footer_logo_5.jpg";
import JetSmartLogo from "../../assets/jetsmart_logo.svg";
import type { FooterItem, Link as FooterLink, PageFooterHeader } from "../../types/cms/common";
import SocialMediaLinks from "./SocialMediaLinks";

type ExpandedSections = Record<number, boolean>;

type FooterProps = {
  footerHeaderData: PageFooterHeader;
};

const Footer: React.FC<FooterProps> = ({ footerHeaderData: initialFooterHeaderData }) => {
  const pathname = usePathname();
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({});
  const [footerHeaderData, setFooterHeaderData] =
    useState<PageFooterHeader>(initialFooterHeaderData);

  useEffect(() => {
    setFooterHeaderData(initialFooterHeaderData);
  }, [initialFooterHeaderData]);

  useEffect(() => {
    const handleLayoutDataUpdated = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.footerHeader) {
        setFooterHeaderData(customEvent.detail.footerHeader);
      }
    };

    window.addEventListener("layoutDataUpdated", handleLayoutDataUpdated);
    return () => {
      window.removeEventListener("layoutDataUpdated", handleLayoutDataUpdated);
    };
  }, []);

  const toggleSection = (idx: number): void => {
    setExpandedSections((prev) => ({
      ...prev,
      [idx]: !prev[idx],
    }));
  };

  const footerLogos = [
    {
      src: Certification_Logo_1,
      show: true,
      alt: "COVID-19 Safety Rating Badge - SKYTRAX",
    },
    {
      src: Certification_Logo_2,
      show: true,
      alt: "Airline of the Year Badge - SKYTRAX 2022",
    },
    { src: Certification_Logo_3, show: true, alt: "Verified Badge - ACHS" },
    {
      src: Certification_Logo_4,
      show: true,
      alt: "eCommerce Award Badge - eCommerce Institute",
    },
    {
      src: Certification_Logo_5,
      show: true,
      alt: "Health Safety Platinum Badge - APEX+",
    },
  ];

  return (
    <footer
      className={cn("text-gray mx-auto w-full bg-white pt-6", {
        "pb-[5rem] lg:pb-0": !isPageNavigationHidden(pathname),
      })}
      data-testid="footer"
    >
      <SocialMediaLinks footerHeaderData={footerHeaderData} />
      <div className="relative">
        <div className="sm:border-gray absolute inset-x-0 sm:border-t sm:border-b" />
        <div className="responsive-padding responsive-layout relative px-4 sm:py-6">
          {/* DESKTOP */}
          <div
            className="hidden grid-cols-1 gap-10 sm:grid-cols-2 md:grid md:grid-cols-4 lg:justify-items-center"
            data-testid="desktop-footer-links"
          >
            {footerHeaderData &&
              footerHeaderData.footer.footerNavigation.items.map(
                (section: FooterItem, id: number) => {
                  const isExpanded = !!expandedSections[id];
                  const initialLinks = section.links.slice(0, INITIAL_COUNT);
                  const extraLinks = section.links.slice(INITIAL_COUNT);

                  return (
                    <div key={section.codename} className="w-full">
                      <h3 className="text-brand-blue text-sm font-semibold">{section.text}</h3>

                      <ul className="-space-y-1.5">
                        {initialLinks.map((link: FooterLink) => (
                          <li key={link.codename} className="py-1">
                            <Link
                              prefetch={false}
                              href={link.url}
                              className="text-brand-gray hover:text-brand-cyan relative inline-block text-xs transition-colors duration-600 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-gray-400 after:transition-[width,background-color] after:duration-700 after:ease-in-out hover:after:w-full hover:after:bg-black"
                            >
                              {link.text}
                            </Link>
                          </li>
                        ))}
                      </ul>

                      {extraLinks.length > 0 && (
                        <ul
                          className={`-space-y-1.5 overflow-hidden transition-[max-height] ${
                            isExpanded ? "max-h-screen duration-[2s]" : "max-h-0 duration-500"
                          }`}
                        >
                          {extraLinks.map((link: FooterLink) => (
                            <li key={link.codename} className="py-1">
                              <Link
                                prefetch={false}
                                href={link.url}
                                className="text-brand-gray hover:text-brand-cyan relative inline-block text-xs transition-colors duration-600 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-gray-400 after:transition-[width,background-color] after:duration-700 after:ease-in-out hover:after:w-full hover:after:bg-black"
                              >
                                {link.text}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}

                      {section.links.length > INITIAL_COUNT && (
                        <button
                          type="button"
                          onClick={() => toggleSection(id)}
                          className="text-brand-gray mt-1 text-sm italic hover:underline"
                        >
                          {isExpanded
                            ? "Show less"
                            : footerHeaderData.footer.footerNavigation.seeMore || "See more"}
                        </button>
                      )}
                    </div>
                  );
                },
              )}
          </div>

          {/* MOBILE */}
          <div className="block md:hidden">
            <Accordion type="single" collapsible className="py-2" data-testid="mobile-footer-links">
              {footerHeaderData?.footer.footerNavigation.items.map((section: FooterItem) => (
                <FooterAccordionItem key={section.codename} value={section.codename}>
                  <FooterAccordionTrigger className="text-brand-blue flex w-full items-center justify-between text-sm font-semibold">
                    {section.text}
                  </FooterAccordionTrigger>

                  <AccordionContent>
                    <ul className="space-y-1.5">
                      {section.links.map((link: FooterLink) => (
                        <li key={link.codename}>
                          <Link
                            prefetch={false}
                            href={link.url}
                            className="text-brand-gray hover:text-brand-cyan text-xs transition"
                          >
                            {link.text}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </FooterAccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
        <div className="border-brand-gray absolute inset-x-0 border-t border-b" />
      </div>
      <div className="responsive-layout responsive-padding py-4 lg:py-2">
        <div className="grid grid-cols-1 items-center justify-items-center gap-2 sm:gap-4 md:grid-cols-3 md:gap-2">
          <div className="text-brand-blue flex flex-row items-center gap-2 text-sm font-semibold md:justify-self-start">
            <Copyright /> {new Date().getFullYear()} JetSMART Chile
          </div>
          <div className="flex flex-row gap-2 justify-self-center">
            {footerLogos.map(
              (logo) =>
                logo.show && (
                  <Image
                    key={logo.alt}
                    src={logo.src}
                    alt={logo.alt}
                    height={120}
                    width={120}
                    unoptimized
                  />
                ),
            )}
          </div>
          <div className="md:justify-self-end">
            <Image width={120} height={120} src={JetSmartLogo} alt="JetSmart Logo" unoptimized />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
